// delSocketService.js
// Centralized Socket.IO handling for DEL form realtime sync

import { io } from "socket.io-client";

let socket = null;
let updateHandler = null; // function(patch) => void - for incoming DEL section broadcasts
let jsaUpdateHandler = null; // function(patch) => void - for incoming JSA broadcasts
let wasteDisposalUpdateHandler = null; // function(patch) => void - for incoming Waste Disposal broadcasts
let photosUpdateHandler = null; // function(patch) => void - for incoming Photo broadcasts
let connectHandler = null; // function(connected:boolean) => void
let visibilityHandler = null; // prevents Heroku H15 idle connection errors on iPad

const BACKEND_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : window.location.origin);

export const initializeDelSocket = () => {
  if (socket) return socket;
  socket = io(BACKEND_URL, {
    withCredentials: true,
    transports: ["websocket", "polling"],
    path: "/socket.io",
    reconnectionAttempts: 10,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
  });

  socket.on("connect", () => {
    if (typeof connectHandler === "function") connectHandler(true);
  });
  socket.on("disconnect", () => {
    if (typeof connectHandler === "function") connectHandler(false);
  });

  // Listen for incoming DEL section broadcasts (project, employees, units, field notes, signatures)
  socket.on("del_update", (patchData) => {
    if (typeof updateHandler === "function") updateHandler(patchData);
  });

  // Listen for JSA broadcasts in DEL context
  socket.on("del_jsa_update", (patchData) => {
    if (typeof jsaUpdateHandler === "function") jsaUpdateHandler(patchData);
  });

  // Listen for Waste Disposal broadcasts in DEL context
  socket.on("del_waste_disposal_update", (patchData) => {
    if (typeof wasteDisposalUpdateHandler === "function") {
      wasteDisposalUpdateHandler(patchData);
    }
  });

  // Listen for Photo broadcasts in DEL context
  socket.on("del_photos_update", (patchData) => {
    if (typeof photosUpdateHandler === "function") {
      photosUpdateHandler(patchData);
    }
  });

  // Proactively disconnect when page goes to background (iPad/mobile)
  // to prevent Heroku H15 idle connection errors from zombie connections
  visibilityHandler = () => {
    if (!socket) return;
    if (document.hidden) {
      socket.disconnect();
    } else {
      socket.connect();
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  return socket;
};

// Register handler for incoming DEL broadcasts (routes to store's handleBroadcast)
export const onDelUpdate = (fn) => {
  updateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for incoming JSA broadcasts
export const onDelJsaUpdate = (fn) => {
  jsaUpdateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for incoming Waste Disposal broadcasts
export const onDelWasteDisposalUpdate = (fn) => {
  wasteDisposalUpdateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for incoming Photo broadcasts
export const onDelPhotosUpdate = (fn) => {
  photosUpdateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for connection status changes
export const onDelConnectionChange = (fn) => {
  connectHandler = typeof fn === "function" ? fn : null;
  if (connectHandler && socket) {
    connectHandler(Boolean(socket.connected));
  }
};

// Clear all consumer handlers to avoid dangling callbacks after unmount
export const clearDelHandlers = () => {
  updateHandler = null;
  jsaUpdateHandler = null;
  wasteDisposalUpdateHandler = null;
  photosUpdateHandler = null;
  connectHandler = null;
};

export const isDelSocketConnected = () => Boolean(socket && socket.connected);

export const joinDel = (delId) =>
  new Promise((resolve) => {
    if (!socket) initializeDelSocket();
    if (!socket)
      return resolve({ success: false, error: "socket_init_failed" });
    socket.emit("join_del", { del_id: delId }, (ack) => {
      resolve(ack || { success: false });
    });
  });

export const leaveDel = (delId) =>
  new Promise((resolve) => {
    if (!socket) return resolve({ success: true });
    socket.emit("leave_del", { del_id: delId }, (ack) => {
      resolve(ack || { success: true });
    });
  });

export const emitDelPatch = (delId, patch) =>
  new Promise((resolve) => {
    if (!socket || !socket.connected) {
      // No connection - should queue for retry
      return resolve({ sent: false, shouldQueue: true, error: null, data: null });
    }

    const payload = { del_id: delId, ...patch };

    // Add timeout for no response (network issues)
    const timeout = setTimeout(() => {
      resolve({ sent: true, shouldQueue: true, error: "timeout", data: null });
    }, 10000); // 10 second timeout

    socket.emit("autosave_del", payload, (ack) => {
      clearTimeout(timeout);
      if (!ack) {
        // No ack at all - network issue, should queue
        resolve({ sent: true, shouldQueue: true, error: "no_response", data: null });
      } else if (ack.success) {
        // Success - don't queue, include full response data
        resolve({ sent: true, shouldQueue: false, error: null, data: ack });
      } else {
        // Server responded with error (validation, etc.) - DON'T queue
        resolve({ sent: true, shouldQueue: false, error: ack.error, data: ack });
      }
    });
  });

export const disconnectDelSocket = () => {
  if (socket) {
    try {
      socket.off("del_update");
      socket.off("del_jsa_update");
      socket.off("del_waste_disposal_update");
      socket.off("del_photos_update");
      socket.off("connect");
      socket.off("disconnect");
    } catch (e) {
      console.error("Error disconnecting socket:", e);
    }
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    clearDelHandlers();
    socket.disconnect();
    socket = null;
  }
};
